import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useCallback, useReducer, useState } from 'react';
import { BlockEvent, ChatRequestSchema, ChatService, ChunkType } from '../gen/llm/v1/chat_pb';
import { createAuthApiTransport } from '../transport';

// --- Block & Message Types ---

export type BlockType =
  | 'content'
  | 'thinking'
  | 'citation'
  | 'tool_use'
  | 'plan'
  | 'tool_status'
  | 'approval'
  | 'error';

export type BlockStatus = 'streaming' | 'complete' | 'error';

export interface Block {
  id: string;
  type: BlockType;
  status: BlockStatus;
  content: string;
  metadata?: Record<string, unknown>;
}

export interface BlockMessage {
  id: string;
  role: 'user' | 'assistant';
  blocks: Block[];
  isStreaming: boolean;
  inputTokens?: number;
  outputTokens?: number;
}

// --- Reducer ---

type ChatAction =
  | { type: 'ADD_USER_MESSAGE'; id: string; content: string }
  | { type: 'START_ASSISTANT'; id: string }
  | { type: 'BLOCK_START'; messageId: string; blockId: string; blockType: BlockType }
  | { type: 'BLOCK_DELTA'; messageId: string; blockId: string; content: string }
  | { type: 'BLOCK_END'; messageId: string; blockId: string }
  | { type: 'STREAM_DONE'; messageId: string; inputTokens?: number; outputTokens?: number }
  | { type: 'STREAM_ERROR'; messageId: string }
  | { type: 'CLEAR' }
  | { type: 'HYDRATE'; messages: BlockMessage[] };

function chatReducer(state: BlockMessage[], action: ChatAction): BlockMessage[] {
  switch (action.type) {
    case 'ADD_USER_MESSAGE':
      return [
        ...state,
        {
          id: action.id,
          role: 'user',
          blocks: [
            { id: `${action.id}_0`, type: 'content', status: 'complete', content: action.content },
          ],
          isStreaming: false,
        },
      ];

    case 'START_ASSISTANT':
      return [
        ...state,
        {
          id: action.id,
          role: 'assistant',
          blocks: [],
          isStreaming: true,
        },
      ];

    case 'BLOCK_START':
      return state.map((m) =>
        m.id === action.messageId
          ? {
              ...m,
              blocks: [
                ...m.blocks,
                {
                  id: action.blockId,
                  type: action.blockType,
                  status: 'streaming' as const,
                  content: '',
                },
              ],
            }
          : m,
      );

    case 'BLOCK_DELTA':
      return state.map((m) =>
        m.id === action.messageId
          ? {
              ...m,
              blocks: m.blocks.map((b) =>
                b.id === action.blockId ? { ...b, content: b.content + action.content } : b,
              ),
            }
          : m,
      );

    case 'BLOCK_END':
      return state.map((m) =>
        m.id === action.messageId
          ? {
              ...m,
              blocks: m.blocks.map((b) =>
                b.id === action.blockId ? { ...b, status: 'complete' as const } : b,
              ),
            }
          : m,
      );

    case 'STREAM_DONE':
      return state.map((m) =>
        m.id === action.messageId
          ? {
              ...m,
              isStreaming: false,
              inputTokens: action.inputTokens,
              outputTokens: action.outputTokens,
              blocks: m.blocks.map((b) =>
                b.status === 'streaming' ? { ...b, status: 'complete' as const } : b,
              ),
            }
          : m,
      );

    case 'STREAM_ERROR':
      return state.map((m) =>
        m.id === action.messageId
          ? {
              ...m,
              isStreaming: false,
              blocks: m.blocks.map((b) =>
                b.status === 'streaming' ? { ...b, status: 'error' as const } : b,
              ),
            }
          : m,
      );

    case 'CLEAR':
      return [];

    case 'HYDRATE':
      return action.messages;

    default:
      return state;
  }
}

// --- ChunkType to BlockType mapping ---

function chunkTypeToBlockType(ct: ChunkType): BlockType {
  switch (ct) {
    case ChunkType.CONTENT:
      return 'content';
    case ChunkType.THINKING:
      return 'thinking';
    case ChunkType.CITATION:
      return 'citation';
    case ChunkType.TOOL_USE:
      return 'tool_use';
    default:
      return 'content';
  }
}

// --- Hook ---

/**
 * Hook for interacting with the LLM chat streaming API.
 * Returns block-structured messages for rich rendering.
 *
 * @param accessToken - JWT access token (null while loading)
 */
export function useChatService(accessToken: string | null) {
  const [messages, dispatch] = useReducer(chatReducer, []);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const sendMessage = useCallback(
    async (
      content: string,
      options?: {
        systemPrompt?: string;
        model?: string;
        temperature?: number;
      },
    ) => {
      if (!accessToken) {
        setError(new Error('Authentication required'));
        return;
      }

      const userId = crypto.randomUUID();
      const assistantId = crypto.randomUUID();

      dispatch({ type: 'ADD_USER_MESSAGE', id: userId, content });
      dispatch({ type: 'START_ASSISTANT', id: assistantId });
      setIsStreaming(true);
      setError(null);

      try {
        const transport = createAuthApiTransport(accessToken);
        const client = createClient(ChatService, transport);
        const request = create(ChatRequestSchema, {
          content,
          systemPrompt: options?.systemPrompt ?? '',
          model: options?.model ?? '',
          temperature: options?.temperature ?? 0,
        });

        // Track which blocks we've already started so we don't re-dispatch BLOCK_START
        // for mid-block chunks that don't carry a block event.
        const knownBlocks = new Set<string>();

        for await (const response of client.chat(request)) {
          const blockId = response.blockId;
          const blockType = chunkTypeToBlockType(response.type);

          // Start a new block when the server signals BLOCK_EVENT_START
          if (response.blockEvent === BlockEvent.START && blockId && !knownBlocks.has(blockId)) {
            knownBlocks.add(blockId);
            dispatch({ type: 'BLOCK_START', messageId: assistantId, blockId, blockType });
          }

          // Append content delta
          if (response.content && blockId) {
            dispatch({
              type: 'BLOCK_DELTA',
              messageId: assistantId,
              blockId,
              content: response.content,
            });
          }

          // Close block when the server signals BLOCK_EVENT_END
          if (response.blockEvent === BlockEvent.END && blockId) {
            dispatch({ type: 'BLOCK_END', messageId: assistantId, blockId });
          }

          // Final chunk: mark the whole message as done
          if (response.done) {
            dispatch({
              type: 'STREAM_DONE',
              messageId: assistantId,
              inputTokens: response.usage?.inputTokens,
              outputTokens: response.usage?.outputTokens,
            });
            break;
          }
        }
      } catch (err) {
        setError(err as Error);
        dispatch({ type: 'STREAM_ERROR', messageId: assistantId });
      } finally {
        setIsStreaming(false);
      }
    },
    [accessToken],
  );

  const clearMessages = useCallback(() => {
    dispatch({ type: 'CLEAR' });
    setError(null);
  }, []);

  const hydrate = useCallback((msgs: BlockMessage[]) => {
    dispatch({ type: 'HYDRATE', messages: msgs });
  }, []);

  return { messages, sendMessage, clearMessages, hydrate, isStreaming, error };
}
